import type { FC } from 'react'

import { Box, Tag, Text, dimensions } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'
import locations from 'modules/locations/store/collections/locations'
import ColoredTag from 'modules/org_chart/components/ColoredTag'
import type { EmployeeNodeData } from 'modules/org_chart/models/NodeData'
import type { TagInfoProp } from 'modules/org_chart/models/TagInfoProp'
import { tagInfoProps } from 'modules/org_chart/models/TagInfoProp'

import { NODE_SIZE } from 'modules/org_chart/components/Node/constants'
import Avatar from './Avatar'
import Circle from './Circle'
import CollapsedToggle from './CollapsedToggle'
import Line from './Line'

export type NodeStatus = 'highlighted' | 'dimmed'

interface Props {
  data: EmployeeNodeData
  tagInfoProp: TagInfoProp | null
  status?: NodeStatus
  x?: number
  y?: number
  onCollapseChange: () => void
  onClick: () => void
}

const IS_BROWSER_SAFARI = navigator.userAgent.match(/safari/i)

const translateGender = (gender: string | null): string | null => {
  switch (gender) {
    case 'male':
      return i18n.t('common.male')
    case 'female':
      return i18n.t('common.female')
    default:
      return gender
  }
}

const Employee: FC<Props> = ({
  data,
  tagInfoProp,
  status,
  x,
  y,
  onCollapseChange,
  onClick,
}) => {
  const [width, height] = NODE_SIZE

  const { name, avatar, job, childrenLength, isUncollapsed } = useUnsafeMemo(() => {
    const employee = data.employee
    const job = getEmployeeJobRole(employee.referenceContract)

    return {
      name: employee.fullName,
      avatar: employee.avatar?.url ?? '',
      job,
      childrenLength: data.childrenLength,
      isUncollapsed: data.isUncollapsed,
    }
  }, [data])

  const tagInfo = useUnsafeMemo<string | null>(() => {
    const employee = data.employee

    switch (tagInfoProp) {
      case tagInfoProps.workplace: {
        if (!employee.locationId) return null
        const location = locations.get(employee.locationId)
        return location?.name || null
      }
      case tagInfoProps.legal_entity: {
        return employee.legalEntity?.legalName || null
      }
      case tagInfoProps.legal_gender:
        return translateGender(employee.gender ?? null)
      case tagInfoProps.start_day:
        return employee.seniorityCalculationDate || employee.referenceContract?.startsOn || '-'
      default:
        return null
    }
  }, [tagInfoProp, data.employee])

  return (
    <foreignObject style={{ overflow: 'visible' }} width={width} height={height} x={x} y={y}>
      <Box
        style={{ opacity: !IS_BROWSER_SAFARI && status === 'dimmed' ? '0.25' : undefined }}
        flexDirection="column"
        alignItems="center"
        paddingX="s8"
      >
        <Box paddingBottom="s4" onClick={onClick} cursor="pointer">
          <Avatar isHighlighted={status === 'highlighted'} src={avatar} alt={name} />
        </Box>
        <Box paddingBottom="s2" textAlign="center" width="s200">
          <Text.Normal size="300" weight="bold" color="grey1000" ellipsis className="notranslate">
            {name}
          </Text.Normal>
        </Box>
        <Box paddingBottom="s8" textAlign="center" width="s200">
          <Text.Normal size="100" weight="normal" color="grey1000" ellipsis>
            {job}
          </Text.Normal>
        </Box>
        <Box flexDirection="row" justifyContent="center" gap="s4" width="full">
          {data.hierarchyLength > 0 && (
            <Box>
              <Tag type="grey">{data.hierarchyLength.toString()}</Tag>
            </Box>
          )}
          {tagInfo && <ColoredTag>{tagInfo}</ColoredTag>}
        </Box>
      </Box>
      {childrenLength > 0 && (
        <Box paddingTop="s8" alignItems="center">
          <Circle />
          <Line height={36} />
          <CollapsedToggle isUncollapsed={isUncollapsed} onChange={onCollapseChange}>
            {childrenLength}
          </CollapsedToggle>
          {isUncollapsed && <Line height={parseInt(job ? dimensions.s24 : dimensions.s48)} />}
        </Box>
      )}
    </foreignObject>
  )
}

export default observer(Employee)
